import React, { createContext, useContext, useMemo } from "react";

import { LOCAL_STORAGE_KEYS } from "../../../constants";
import { useDashboardClusters, useLocalStorageState } from "../../../hooks";
import { type DashboardClustersResponse } from "../../../types";
import { TimeRange } from "../../ClusterDetail/Components/TimeRangeSelector";

export type ClusterDashboardContextType = {
  dashboardClusters: DashboardClustersResponse;
  dashboardClusters30d: DashboardClustersResponse;
  initialLoading: boolean;
  loading: boolean;
  clustersLoading: boolean;
  clusters30dLoading: boolean;
  timeRange: TimeRange;
  setTimeRange: (timeRange: TimeRange) => void;
};

export const ClusterDashboardContext = createContext<ClusterDashboardContextType>({
  dashboardClusters: {
    clusters: [],
    summary: {
      cost: 0,
      previousCost: 0,
      waste: 0,
      previousWaste: 0,
      potentialSavings: 0,
    },
  },
  dashboardClusters30d: {
    clusters: [],
    summary: {
      cost: 0,
      previousCost: 0,
      waste: 0,
      previousWaste: 0,
      potentialSavings: 0,
    },
  },
  loading: false,
  initialLoading: true,
  clustersLoading: false,
  clusters30dLoading: false,
  timeRange: TimeRange.T_30D,
  setTimeRange: () => {},
});

interface ClusterDashboardContextProviderProps {
  children: React.ReactNode;
}

export const ClusterDashboardContextProvider = ({ children }: ClusterDashboardContextProviderProps) => {
  const [timeRange, setTimeRange] = useLocalStorageState(TimeRange.T_30D, LOCAL_STORAGE_KEYS.K8S_LENS_TIME_RANGE);

  const {
    dashboardClusters,
    loading: dashboardClustersLoading,
    initialLoading: initialDashboardClustersLoading,
  } = useDashboardClusters(timeRange);
  const { dashboardClusters: dashboardClusters30d, loading: dashboardClusters30dLoading } = useDashboardClusters(
    TimeRange.T_30D
  );

  const value: ClusterDashboardContextType = useMemo(
    () => ({
      dashboardClusters,
      dashboardClusters30d,
      timeRange,
      setTimeRange,
      loading: dashboardClustersLoading || dashboardClusters30dLoading,
      initialLoading: initialDashboardClustersLoading,
      clustersLoading: dashboardClustersLoading,
      clusters30dLoading: dashboardClusters30dLoading,
    }),
    [
      dashboardClusters,
      timeRange,
      setTimeRange,
      dashboardClustersLoading,
      dashboardClusters30d,
      dashboardClusters30dLoading,
      initialDashboardClustersLoading,
    ]
  );

  return <ClusterDashboardContext.Provider value={value}>{children}</ClusterDashboardContext.Provider>;
};

export const useClusterDashboardContext = () => {
  const context = useContext(ClusterDashboardContext);

  if (!context) {
    throw new Error("useClusterDashboardContext must be used within a ClusterDashboardContextProvider");
  }

  return context;
};
